import React, { useState, useEffect, useMemo } from 'react';
import { Navigation, Header } from '../common';
import { DataTable } from './';
import { mockData } from '../../data';
import { 
  fetchCompanySales,
  fetchSellerSales,
  fetchGroupSales,
  fetchParentSales,
  transformApiDataToFrontend,
  transformGroupsDataToFrontend,
  transformParentSalesToFrontend
} from '../../services/api';

const Dashboard = ({ user, onLogout }) => {
  // Dashboard state
  const [activeTab, setActiveTab] = useState('home');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [timePeriod, setTimePeriod] = useState('weekly');
  
  // API data state
  const [apiData, setApiData] = useState(null);
  const [sellerData, setSellerData] = useState({});
  const [groupsData, setGroupsData] = useState({});
  const [parentProductsData, setParentProductsData] = useState({});
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataError, setDataError] = useState(null);
  
  // Filter state
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedMetrics, setSelectedMetrics] = useState(['all']);
  const [selectedCompanies, setSelectedCompanies] = useState(['all']);
  const [selectedGroups, setSelectedGroups] = useState(['all']);
  const [metricsDropdownOpen, setMetricsDropdownOpen] = useState(false);
  const [companiesDropdownOpen, setCompaniesDropdownOpen] = useState(false);
  const [groupsDropdownOpen, setGroupsDropdownOpen] = useState(false);

  // Available metrics for filtering
  const availableMetrics = [
    { id: 'all', label: 'All Metrics' },
    { id: 'totalSales', label: 'Total Sales' },
    { id: 'unitsSold', label: 'Units Sold' },
    { id: 'aov', label: 'AOV' },
    { id: 'adSpend', label: 'Ad Spend' },
    { id: 'tacos', label: 'Tacos' },
    { id: 'profitPercent', label: 'Profit %' },
    { id: 'profitTotal', label: 'Profit Total' }
  ];

  // Fetch API data when component mounts
  useEffect(() => {
    const fetchData = async () => {
      setIsLoadingData(true);
      setDataError(null);
      
      try {
        console.log('🚀 Fetching sales data from new API endpoints...');
        
        // Fetch all endpoints in parallel
        const [companyResponse, sellerResponse, groupsResponse, parentSalesResponse] = await Promise.all([
          fetchCompanySales(),
          fetchSellerSales(),
          fetchGroupSales(),
          fetchParentSales()
        ]);
        
        console.log('📥 Company API Response:', companyResponse);
        console.log('📥 Seller API Response:', sellerResponse);
        console.log('📥 Groups API Response:', groupsResponse);
        console.log('📥 Parent Sales API Response:', parentSalesResponse);
        
        // Transform company data
        const transformedCompanyData = transformApiDataToFrontend(companyResponse);
        console.log('✅ Transformed Company Data:', transformedCompanyData);
        setApiData(transformedCompanyData);
        
        // Transform seller data
        const transformedSellerData = {
          'tps-nutrients': transformApiDataToFrontend(sellerResponse)
        };
        console.log('✅ Transformed Seller Data:', transformedSellerData);
          setSellerData(transformedSellerData);
        
        // Transform groups data
        const transformedGroups = transformGroupsDataToFrontend(groupsResponse);
        console.log('✅ Transformed Groups Data:', transformedGroups);
        console.log('✅ Group IDs:', Object.keys(transformedGroups));
        console.log('✅ TPS Nutrients Group Data:', transformedGroups['tps-nutrients-group']);
        console.log('✅ Bloom City Group Data:', transformedGroups['bloom-city']);
        
        // Wrap groups under seller structure
        const groupsUnderSeller = {
          'tps-nutrients': transformedGroups
        };
        setGroupsData(groupsUnderSeller);
        console.log('✅ Groups wrapped under seller:', groupsUnderSeller);
        
        // Transform parent sales data
        const transformedParentSales = transformParentSalesToFrontend(parentSalesResponse);
        console.log('✅ Transformed Parent Sales:', transformedParentSales);
        
        // Log parent counts
        const tpsParents = transformedParentSales['TPS Nutrients'] || [];
        const bloomParents = transformedParentSales['Bloom City'] || [];
        console.log(`📊 TPS Nutrients: ${tpsParents.length} parents`);
        console.log(`📊 Bloom City: ${bloomParents.length} parents`);
        
        // Extract all children as flat arrays for child view
        const tpsChildrenFlat = [];
        tpsParents.forEach(parent => {
          if (parent.variations && parent.variations.length > 0) {
            tpsChildrenFlat.push(...parent.variations);
          }
        });
        
        const bloomChildrenFlat = [];
        bloomParents.forEach(parent => {
          if (parent.variations && parent.variations.length > 0) {
            bloomChildrenFlat.push(...parent.variations);
          }
        });
        
        console.log(`📊 TPS Nutrients: ${tpsChildrenFlat.length} total children/variations`);
        console.log(`📊 Bloom City: ${bloomChildrenFlat.length} total children/variations`);
        
        // Wrap parent sales under seller/group structure
        const parentSalesUnderSeller = {
            'tps-nutrients': {
              'tps-nutrients-group': {
              parent: tpsParents,
              child: tpsChildrenFlat  // Flattened children for child-only view
              },
              'bloom-city': {
              parent: bloomParents,
              child: bloomChildrenFlat  // Flattened children for child-only view
            }
          }
        };
        setParentProductsData(parentSalesUnderSeller);
        console.log('✅ Parent sales wrapped under seller:', parentSalesUnderSeller);
        
        console.log('✨ All data loaded and transformed successfully!');
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setDataError(error.message);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, []);

  // Get the current data source (merged mock data with API data) - memoized
  const getCurrentData = useMemo(() => {
    // Start with mock data structure
    const data = JSON.parse(JSON.stringify(mockData));
    
    // Merge company data into The Jungle Group's parent company data
    if (apiData && data.parentCompanies && data.parentCompanies['jungle-group']) {
      console.log('🔄 Merging company data into Jungle Group');
      data.parentCompanies['jungle-group'].data = apiData;
    }
    
    // Merge seller account data
    if (Object.keys(sellerData).length > 0 && data.companyAccounts && data.companyAccounts['jungle-group']) {
      console.log('🔄 Merging seller data');
      Object.keys(sellerData).forEach(sellerKey => {
        if (data.companyAccounts['jungle-group'][sellerKey]) {
          data.companyAccounts['jungle-group'][sellerKey].data = sellerData[sellerKey];
        }
      });
    }
    
    // Merge groups/brands data
    if (Object.keys(groupsData).length > 0 && data.companyAccounts && data.companyAccounts['jungle-group']) {
      console.log('🔄 Merging groups data:', groupsData);
      Object.keys(groupsData).forEach(sellerKey => {
        if (data.companyAccounts['jungle-group'][sellerKey] && data.companyAccounts['jungle-group'][sellerKey].groups) {
          const sellerGroups = groupsData[sellerKey];
          console.log(`🔄 Seller "${sellerKey}" has groups:`, Object.keys(sellerGroups));
          
          data.companyAccounts['jungle-group'][sellerKey].groups = 
            data.companyAccounts['jungle-group'][sellerKey].groups.map(group => {
              const groupData = sellerGroups[group.id];
              console.log(`🔄 Looking for group.id "${group.id}" (${group.name}) in API data:`, groupData ? 'FOUND' : 'NOT FOUND');
              if (groupData) {
                console.log(`✅ Merging data for group "${group.id}" (${group.name}):`, groupData);
                return {
                  ...group,
                  data: groupData
                };
              }
              return group;
            });
          console.log(`✅ Merged groups for seller ${sellerKey}:`, data.companyAccounts['jungle-group'][sellerKey].groups);
        }
      });
    }
    
    // Merge parent products data
    console.log('🔍 Checking parent products data:', parentProductsData);
    console.log('🔍 Parent products data keys:', Object.keys(parentProductsData || {}));
    if (parentProductsData && Object.keys(parentProductsData).length > 0 && data.companyAccounts && data.companyAccounts['jungle-group']) {
      console.log('🔄 Merging parent products data:', parentProductsData);
      Object.keys(parentProductsData).forEach(sellerKey => {
        if (data.companyAccounts['jungle-group'][sellerKey] && data.companyAccounts['jungle-group'][sellerKey].groups) {
          const sellerParentProducts = parentProductsData[sellerKey];
          console.log(`🔄 Processing parent products for seller ${sellerKey}:`, sellerParentProducts);
          console.log(`🔍 Parent products keys:`, Object.keys(sellerParentProducts));
          
          data.companyAccounts['jungle-group'][sellerKey].groups = 
            data.companyAccounts['jungle-group'][sellerKey].groups.map(group => {
              const parentProducts = sellerParentProducts[group.id];
          console.log(`🔍 Looking for parent products for group ${group.id}:`, parentProducts);
              const hasProducts = parentProducts && ((parentProducts.parent && parentProducts.parent.length > 0) || (parentProducts.child && parentProducts.child.length > 0));
              if (hasProducts) {
                const count = (parentProducts.parent?.length || 0) + (parentProducts.child?.length || 0);
                console.log(`✅ Merging products for group ${group.id}:`, count, 'products');
                return {
                  ...group,
                  products: parentProducts
                };
              } else {
            console.log(`⚠️ No parent products found for group ${group.id}`);
          }
              return group;
            });
          console.log(`✅ Merged parent products for seller ${sellerKey}`);
        }
      });
    } else {
      console.log('⚠️ No parent products data to merge:', {
        hasParentProducts: !!parentProductsData,
        parentProductsKeys: Object.keys(parentProductsData || {}),
        hasCompanyAccounts: !!data.companyAccounts,
        hasJungleGroup: !!data.companyAccounts?.['jungle-group']
      });
    }
    
    return data;
  }, [apiData, sellerData, groupsData, parentProductsData]);

  // Get all available companies for filtering
  const getAvailableCompanies = () => {
    const currentData = getCurrentData;
    const companyAccounts = currentData?.companyAccounts?.['jungle-group'];
    if (!companyAccounts) return [];
    
    const companies = Object.values(companyAccounts).map(company => ({
      id: company.id,
      name: company.name
    }));
    
    return sortOrder === 'asc' 
      ? companies.sort((a, b) => a.name.localeCompare(b.name))
      : companies.sort((a, b) => b.name.localeCompare(a.name));
  };

  // Get all available groups for filtering
  const getAvailableGroups = () => {
    const currentData = getCurrentData;
    const companyAccounts = currentData?.companyAccounts?.['jungle-group'];
    if (!companyAccounts) return [];
    
    const groups = [];
    Object.values(companyAccounts).forEach(company => {
      if (company.groups && company.groups.length > 0) {
        company.groups.forEach(group => {
          groups.push({
            id: group.id,
            name: group.name,
            companyName: company.name
          });
        });
      }
    });
    
    return sortOrder === 'asc' 
      ? groups.sort((a, b) => a.name.localeCompare(b.name))
      : groups.sort((a, b) => b.name.localeCompare(a.name));
  };

  // Handle metric filtering
  const handleMetricToggle = (metricId) => {
    if (metricId === 'all') {
      setSelectedMetrics(['all']);
    } else {
      setSelectedMetrics(prev => {
        const newMetrics = prev.filter(id => id !== 'all');
        if (newMetrics.includes(metricId)) {
          return newMetrics.filter(id => id !== metricId);
        } else {
          return [...newMetrics, metricId];
        }
      });
    }
  };

  // Handle company filtering
  const handleCompanyToggle = (companyId) => {
    if (companyId === 'all') {
      setSelectedCompanies(['all']);
    } else {
      setSelectedCompanies(prev => {
        const newCompanies = prev.filter(id => id !== 'all');
        if (newCompanies.includes(companyId)) {
          const filtered = newCompanies.filter(id => id !== companyId);
          return filtered.length === 0 ? ['all'] : filtered;
        } else {
          return [...newCompanies, companyId];
        }
      });
    }
  };

  // Handle group filtering
  const handleGroupToggle = (groupId) => {
    if (groupId === 'all') {
      setSelectedGroups(['all']);
    } else {
      setSelectedGroups(prev => {
        const newGroups = prev.filter(id => id !== 'all');
        if (newGroups.includes(groupId)) {
          const filtered = newGroups.filter(id => id !== groupId);
          return filtered.length === 0 ? ['all'] : filtered;
        } else {
          return [...newGroups, groupId];
        }
      });
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (metricsDropdownOpen && !event.target.closest('.metrics-dropdown')) {
        setMetricsDropdownOpen(false);
      }
      if (companiesDropdownOpen && !event.target.closest('.companies-dropdown')) {
        setCompaniesDropdownOpen(false);
      }
      if (groupsDropdownOpen && !event.target.closest('.groups-dropdown')) {
        setGroupsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [metricsDropdownOpen, companiesDropdownOpen, groupsDropdownOpen]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleCompanyChange = (company) => {
    setSelectedCompany(company);
  };

  const handleTimePeriodChange = (period) => {
    setTimePeriod(period);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 via-white to-primary-50 dark:from-slate-900 dark:via-slate-800 dark:to-indigo-900 transition-colors duration-300">
      {/* Background decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-200 dark:bg-indigo-500 rounded-full mix-blend-multiply dark:mix-blend-overlay filter blur-xl opacity-70 dark:opacity-30 animate-pulse-soft"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary-200 dark:bg-purple-500 rounded-full mix-blend-multiply dark:mix-blend-overlay filter blur-xl opacity-70 dark:opacity-30 animate-pulse-soft animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-success-100 dark:bg-blue-600 rounded-full mix-blend-multiply dark:mix-blend-overlay filter blur-xl opacity-50 dark:opacity-20 animate-pulse-soft animation-delay-4000"></div>
      </div>

      {/* Header */}
      <Header user={user} onLogout={onLogout} />
      
      {/* Main Content */}
      <div className="relative z-10 min-h-screen">
        <div className="max-w-[95%] mx-auto px-8 py-8 pt-24">
          {/* Navigation */}
          <Navigation
            activeTab={activeTab}
            onTabChange={handleTabChange}
            selectedCompany={selectedCompany}
            onCompanyChange={handleCompanyChange}
            timePeriod={timePeriod}
            onTimePeriodChange={handleTimePeriodChange}
          />

          {/* Loading indicator */}
          {isLoadingData && (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center space-x-3 text-primary-600 dark:text-indigo-400">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 dark:border-indigo-400"></div>
                <span className="text-sm font-medium">Loading data...</span>
              </div>
            </div>
          )}

          {/* Error message */}
          {dataError && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-500 dark:text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-red-700 dark:text-red-300">Failed to load data: {dataError}</span>
              </div>
            </div>
          )}

          {/* Data table */}
          <DataTable
            data={getCurrentData}
            activeTab={activeTab}
            selectedCompany={selectedCompany}
            timePeriod={timePeriod}
            onTimePeriodChange={handleTimePeriodChange}
            sortOrder={sortOrder}
            selectedMetrics={selectedMetrics}
            selectedCompanies={selectedCompanies}
            selectedGroups={selectedGroups}
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 
